import {
  AirplaneTicketTwoTone,
  HomeTwoTone,
  HotelTwoTone,
  KeyboardArrowRightTwoTone,
  KeyboardArrowUpTwoTone,
  ReceiptLongTwoTone,
  VerifiedTwoTone,
  WalletTwoTone,
} from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Link as MLink,
  Paper,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { getSalesIndividualOrderInfo, guideInfos } from '../api';
import ChangeBedTypeDialog from '../components/ChangeBedTypeDialog';
import TransferTourDialog from '../components/ChangeTourDialog';
import OnlineTourDetailPresentations from '../components/OnlineTourDetailPresentations';
import { OrderSummary } from '../components/OrderSummary';
import PageHeader from '../components/PageHeader';
import TourPlan from '../components/TourPlan';
import { AuthContext } from '../context/AuthContext';
import {
  IMemberSalesOrderInfoResult,
  ITourGuideInfoResult,
} from '../utils/types';

export default function OrderDetailPage() {
  const { t } = useTranslation();
  const { salesIndividualId } = useParams();
  const [data, setData] = useState<IMemberSalesOrderInfoResult>();
  const [guideInfo, setGuideInfo] = useState<ITourGuideInfoResult[]>();
  const [hasPresentationData, setHasPresentationData] = useState<boolean>(true);
  const [hasActivityData, setHasActivityData] = useState<boolean>(true);
  const [tourActivityCollapsed, setTourActivityCollapsed] = useState(true);
  const [tourPresentationCollapsed, setTourPresentationCollapsed] =
    useState(true);
  const { token } = useContext(AuthContext);
  const [changeBedTypeOpen, setChangeBedTypeOpen] = useState(false);
  const [transferTourOpen, setTransferTourOpen] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferTourOpen, changeBedTypeOpen]);

  useEffect(() => {
    if (salesIndividualId) fetchGuideInfo(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesIndividualId]);

  async function fetchData() {
    if (!salesIndividualId) return;
    const res = await getSalesIndividualOrderInfo(
      salesIndividualId as string,
      token,
    );
    const salesOrderInfo = await res.json();
    setData(salesOrderInfo);
  }

  const fetchGuideInfo = async (id: string) => {
    try {
      const res = await guideInfos(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        setGuideInfo(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      console.error(e);
    }
  };

  if (!salesIndividualId) return null;
  if (!data) return null;

  return (
    <Box>
      <PageHeader title={t('orderDetailPage.title')} />
      <Breadcrumbs sx={{ mb: 1 }}>
        <MLink
          component={Link}
          to="/"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeTwoTone />
        </MLink>
        <MLink component={Link} to="/account">
          {t('nav.account')}
        </MLink>
        <MLink component={Link} to="/account/orders">
          {t('nav.orders')}
        </MLink>
        <Typography>{t('nav.tourId', { tourId: data.tourId })}</Typography>
      </Breadcrumbs>
      <Paper sx={{ maxWidth: 400 }}>
        <OrderSummary
          pnr={data.pnr}
          orderNumber={data.orderNumber}
          orderDate={new Date(data.orderDate)}
          tourInfo={data.tourInfo}
          tourStartDate={new Date(data.tourStartDate)}
          bedType={data.bedType}
          guideInfo={guideInfo}
          onTransferTourClick={() => setTransferTourOpen(true)}
          onBedTypeChangeClick={() => setChangeBedTypeOpen(true)}
        />
      </Paper>

      <List>
        <ListSubheader>{t('orderDetailPage.actions')}</ListSubheader>
        <ListItemButton
          component={Link}
          to={`accommodation?tourId=${data.tourId}`}
        >
          <ListItemIcon>
            <HotelTwoTone />
          </ListItemIcon>
          <ListItemText primary={t('orderDetailPage.accommodation')} />
          <KeyboardArrowRightTwoTone />
        </ListItemButton>
        <ListItemButton
          component={Link}
          to={`transportation?tourId=${data.tourId}`}
        >
          <ListItemIcon>
            <AirplaneTicketTwoTone />
          </ListItemIcon>
          <ListItemText primary={t('orderDetailPage.transportation')} />
          <KeyboardArrowRightTwoTone />
        </ListItemButton>
        <ListItemButton
          component={Link}
          to={`passport?lastName=${data.customerLastName}&orderNumber=${data.orderNumber}&tourId=${data.tourId}`}
        >
          <ListItemIcon>
            <WalletTwoTone />
          </ListItemIcon>
          <ListItemText primary={t('orderDetailPage.passport')} />
          <KeyboardArrowRightTwoTone />
        </ListItemButton>
        <ListItemButton component={Link} to={`visa?tourId=${data.tourId}`}>
          <ListItemIcon>
            <VerifiedTwoTone />
          </ListItemIcon>
          <ListItemText primary={t('orderDetailPage.visa')} />
          <KeyboardArrowRightTwoTone />
        </ListItemButton>
        <ListItemButton
          component={Link}
          to={`invoice?lastName=${data.customerLastName}&orderNumber=${data.orderNumber}&tourId=${data.tourId}`}
        >
          <ListItemIcon>
            <ReceiptLongTwoTone />
          </ListItemIcon>
          <ListItemText primary={t('orderDetailPage.invoice')} />
          <KeyboardArrowRightTwoTone />
        </ListItemButton>
      </List>
      {!hasActivityData && !hasPresentationData && (
        <Typography sx={{ mt: 1 }}>
          {t('onlineTourDetail.noNameYet')}
        </Typography>
      )}
      {hasPresentationData && (
        <>
          <Collapse
            collapsedSize={480}
            in={!tourPresentationCollapsed}
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                mt: 3,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: tourPresentationCollapsed ? 100 : 0,
                  backgroundImage: 'linear-gradient(transparent, white)',
                  pointerEvents: 'none',
                  display: 'block',
                },
              }}
            >
              <OnlineTourDetailPresentations
                tourId={data.tourId}
                onEmptyDetails={(hasData) => setHasPresentationData(hasData)}
              />
            </Box>
          </Collapse>
          <Button
            onClick={() =>
              setTourPresentationCollapsed(!tourPresentationCollapsed)
            }
            sx={{ mt: 2 }}
          >
            {tourPresentationCollapsed
              ? t('orderDetailPage.showMoreDetails')
              : t('orderDetailPage.showLessDetails')}
            {tourPresentationCollapsed ? (
              <KeyboardArrowRightTwoTone />
            ) : (
              <KeyboardArrowUpTwoTone />
            )}
          </Button>
        </>
      )}
      {hasActivityData && (
        <>
          <Collapse
            collapsedSize={480}
            in={!tourActivityCollapsed}
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                mt: 3,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: tourActivityCollapsed ? 100 : 0,
                  backgroundImage: 'linear-gradient(transparent, white)',
                  pointerEvents: 'none',
                  display: 'block',
                },
              }}
            >
              <TourPlan
                salesIndividualId={parseInt(salesIndividualId)}
                onEmptyDetails={(hasData) => setHasActivityData(hasData)}
              ></TourPlan>
            </Box>
          </Collapse>
          <Button
            onClick={() => setTourActivityCollapsed(!tourActivityCollapsed)}
            sx={{ mt: 2 }}
          >
            {tourActivityCollapsed
              ? t('orderDetailPage.showMoreDetails')
              : t('orderDetailPage.showLessDetails')}
            {tourActivityCollapsed ? (
              <KeyboardArrowRightTwoTone />
            ) : (
              <KeyboardArrowUpTwoTone />
            )}
          </Button>
        </>
      )}
      <ChangeBedTypeDialog
        open={changeBedTypeOpen}
        onClose={() => setChangeBedTypeOpen(false)}
        data={data}
      />
      <TransferTourDialog
        open={transferTourOpen}
        onClose={() => setTransferTourOpen(false)}
        data={data}
      />
    </Box>
  );
}
